import { useState, type FormEvent } from "react";
import { Link } from "wouter";
import { Building2, Loader2, LogIn, ShieldAlert, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EnvironmentBadge, PageHead, Panel } from "@/components/bits";
import { useApiResource } from "@/hooks/use-api-resource";
import { useAuth } from "@/features/auth/auth-context";
import { useDashboard } from "@/features/dashboard/dashboard-context";
import { publicApi } from "@/services/api-client";
import {
  Chip,
  Empty,
  ErrorBox,
  MetaList,
  Skeleton,
  errorMessage,
  list,
  rec,
  txt,
  type Rec,
} from "./shared";

type Target = { id: string; type: "INDIVIDUAL" | "BUSINESS"; name: string };

export default function AccountsPage() {
  const { identity, login } = useAuth();
  const d = useDashboard();
  const accounts = useApiResource<Rec>("/accounts");
  const invitations = useApiResource<Rec>("/team/invitations");
  const activeId =
    identity?.accountId ?? txt(rec(accounts.data).active_account_id);
  const active = useApiResource<Rec>(
    activeId ? `/accounts/${encodeURIComponent(activeId)}` : null,
  );
  const items = list(accounts.data, ["accounts"]);
  const invitationItems = list(invitations.data, ["invitations"]);
  const hasBusiness = items.some(
    (a) => txt(a.account_type).toUpperCase() === "BUSINESS",
  );

  const [legal, setLegal] = useState("");
  const [reg, setReg] = useState("");
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeErr, setUpgradeErr] = useState("");
  const [created, setCreated] = useState<Rec | null>(null);

  const [target, setTarget] = useState<Target | null>(null);
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [signing, setSigning] = useState(false);
  const [invitationBusy, setInvitationBusy] = useState("");
  const [invitationError, setInvitationError] = useState("");

  const decideInvitation = async (
    id: string,
    decision: "accept" | "decline",
  ) => {
    setInvitationBusy(id);
    setInvitationError("");
    try {
      await publicApi(
        `/team/invitations/${encodeURIComponent(id)}/${decision}`,
        {
          method: "POST",
          body: {},
        },
      );
      await Promise.all([invitations.reload(), accounts.reload()]);
    } catch (cause) {
      setInvitationError(errorMessage(cause));
    } finally {
      setInvitationBusy("");
    }
  };

  const upgrade = async (e: FormEvent) => {
    e.preventDefault();
    if (legal.trim().length < 2 || reg.trim().length < 2)
      return setUpgradeErr("Raison sociale et numéro RCCM requis.");
    setUpgrading(true);
    setUpgradeErr("");
    try {
      const r = await publicApi<Rec>("/accounts/upgrade-to-business", {
        method: "POST",
        body: { legal_name: legal.trim(), registration_number: reg.trim() },
      });
      setCreated(rec(r.data));
      setLegal("");
      setReg("");
      await Promise.all([accounts.reload(), d.refresh()]);
    } catch (c) {
      setUpgradeErr(errorMessage(c));
    } finally {
      setUpgrading(false);
    }
  };

  const reauth = async (e: FormEvent) => {
    e.preventDefault();
    if (!target) return;
    setSigning(true);
    setLoginErr("");
    try {
      await login({
        email: d.profile.email,
        password,
        account_id: target.id,
        account_type: target.type,
      });
      setPassword("");
      setTarget(null);
    } catch (c) {
      setLoginErr(errorMessage(c, "Connexion impossible."));
    } finally {
      setSigning(false);
    }
  };

  const createdAccount = rec(created?.account);

  return (
    <>
      <PageHead
        title="Comptes"
        sub="Vos espaces individuels et entreprise. Chaque espace a ses propres droits."
      >
        <EnvironmentBadge />
      </PageHead>
      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <div className="space-y-5">
          {invitationItems.length > 0 && (
            <Panel
              title="Invitations en attente"
              sub="Acceptez ou refusez l’accès proposé à un espace entreprise."
            >
              {invitationError && <ErrorBox message={invitationError} />}
              <ul className="space-y-2">
                {invitationItems.map((invitation) => {
                  const id = txt(invitation.id);
                  return (
                    <li
                      key={id}
                      className="flex flex-wrap items-center gap-3 rounded-lg border p-3"
                    >
                      <Building2 className="h-4 w-4 text-primary" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold">
                          {txt(
                            invitation.business_name ?? invitation.account_name,
                            "Espace entreprise",
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Rôle proposé : {txt(invitation.role, "MEMBER")}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={Boolean(invitationBusy)}
                        onClick={() => void decideInvitation(id, "decline")}
                      >
                        Refuser
                      </Button>
                      <Button
                        size="sm"
                        disabled={Boolean(invitationBusy)}
                        onClick={() => void decideInvitation(id, "accept")}
                      >
                        {invitationBusy === id && (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        )}
                        Accepter
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </Panel>
          )}
          <Panel
            title="Mes espaces"
            action={
              <Button
                size="sm"
                variant="outline"
                onClick={() => void accounts.reload()}
              >
                Actualiser
              </Button>
            }
          >
            {accounts.loading ? (
              <Skeleton />
            ) : accounts.error ? (
              <ErrorBox
                message={accounts.error}
                onRetry={() => void accounts.reload()}
              />
            ) : items.length === 0 ? (
              <Empty title="Aucun espace renvoyé" />
            ) : (
              <ul className="space-y-2" data-testid="list-accounts">
                {items.map((a) => {
                  const id = txt(a.id);
                  const type =
                    txt(a.account_type).toUpperCase() === "BUSINESS"
                      ? "BUSINESS"
                      : "INDIVIDUAL";
                  const isActive = id === activeId;
                  return (
                    <li
                      key={id}
                      className={`flex flex-wrap items-center gap-3 rounded-lg border p-3 ${isActive ? "border-primary/40 bg-primary/5" : ""}`}
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                        {type === "BUSINESS" ? (
                          <Building2 className="h-4 w-4" />
                        ) : (
                          <User className="h-4 w-4" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">
                          {txt(a.display_name ?? a.legal_name, "Espace")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {type === "BUSINESS" ? "Entreprise" : "Individuel"} ·{" "}
                          {txt(a.country_code, "—")} · {txt(a.role, "—")}
                        </p>
                      </div>
                      <Chip value={a.status} />
                      <span className="text-[11px] text-muted-foreground">
                        Vérification
                      </span>
                      <Chip value={a.verification_status ?? "NOT_STARTED"} />
                      {isActive ? (
                        <span className="text-xs font-bold text-primary">
                          Actif
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`button-switch-${id}`}
                          onClick={() =>
                            setTarget({
                              id,
                              type,
                              name: txt(
                                a.display_name ?? a.legal_name,
                                "Espace",
                              ),
                            })
                          }
                        >
                          <LogIn className="mr-1.5 h-3.5 w-3.5" />
                          Se reconnecter
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>
          {/* <Panel
            title="Espace actif"
            sub="Seul l'espace actif peut être consulté en détail."
          >
            {active.loading ? (
              <Skeleton rows={2} />
            ) : active.error ? (
              <ErrorBox
                message={active.error}
                onRetry={() => void active.reload()}
              />
            ) : active.data ? (
              <MetaList data={rec(rec(active.data).account ?? active.data)} />
            ) : (
              <Empty title="Aucun détail disponible" />
            )}
            {d.isBusiness && d.locked && (
              <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                <ShieldAlert className="h-4 w-4" />
                Opérations financières verrouillées jusqu'à l'approbation du
                KYB.
                <Link href="/verification" className="font-semibold underline">
                  Compléter le KYB
                </Link>
              </div>
            )}
          </Panel> */}
        </div>

        <Panel
          title="Passer en compte entreprise"
          sub="Convertit votre compte individuel en compte entreprise (statut PENDING jusqu'à validation KYB)."
        >
          {created ? (
            <div className="space-y-3" data-testid="panel-upgrade-result">
              <p className="text-sm font-semibold">
                Compte converti en entreprise
              </p>
              <MetaList data={createdAccount} />
              <p className="text-xs">
                Étape suivante :{" "}
                <span className="font-mono font-semibold">
                  {txt(created.next_step, "—")}
                </span>
              </p>
              <p className="text-xs text-muted-foreground">
                Votre compte est désormais de type entreprise. Complétez votre
                dossier KYB pour débloquer l'ensemble des fonctionnalités
                entreprise.
              </p>
              <Button
                className="w-full"
                data-testid="button-login-new-business"
                disabled={!txt(createdAccount.id)}
                onClick={() =>
                  setTarget({
                    id: txt(createdAccount.id),
                    type: "BUSINESS",
                    name: txt(createdAccount.legal_name, "Entreprise"),
                  })
                }
              >
                <LogIn className="mr-1.5 h-4 w-4" />
                Se reconnecter sur cet espace
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => setCreated(null)}
              >
                Fermer
              </Button>
            </div>
          ) : d.isBusiness ? (
            <Empty
              title="Vous êtes dans un espace entreprise"
              sub="La création d'un espace entreprise se fait depuis votre compte individuel."
            />
          ) : (
            <form
              className="grid gap-3"
              onSubmit={upgrade}
              noValidate
              data-testid="form-upgrade-business"
            >
              {upgradeErr && <ErrorBox message={upgradeErr} />}
              {hasBusiness && (
                <p className="text-xs text-amber-700">
                  Vous possédez déjà au moins un espace entreprise.
                </p>
              )}
              <div className="grid gap-1.5">
                <Label htmlFor="legal">Raison sociale</Label>
                <Input
                  id="legal"
                  data-testid="input-legal-name"
                  value={legal}
                  onChange={(e) => setLegal(e.target.value)}
                  placeholder="Maadoum Consulting SARL"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="reg">Numéro d'immatriculation (RCCM)</Label>
                <Input
                  id="reg"
                  data-testid="input-registration-number"
                  value={reg}
                  onChange={(e) => setReg(e.target.value)}
                  placeholder="RC/DLA/2026/B/1234"
                />
              </div>
              <Button
                type="submit"
                data-testid="button-upgrade-business"
                disabled={upgrading}
              >
                {upgrading && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                )}
                Créer l'espace entreprise
              </Button>
            </form>
          )}
        </Panel>
      </div>

      <Dialog
        open={!!target}
        onOpenChange={(o) => {
          if (!o) {
            setTarget(null);
            setPassword("");
            setLoginErr("");
          }
        }}
      >
        <DialogContent>
          <form onSubmit={reauth} data-testid="form-reauth">
            <DialogHeader>
              <DialogTitle>Ouvrir « {target?.name} »</DialogTitle>
              <DialogDescription>
                Saisissez votre mot de passe pour une nouvelle connexion sur cet
                espace (
                {target?.type === "BUSINESS" ? "entreprise" : "individuel"}).
              </DialogDescription>
            </DialogHeader>
            <div className="my-4 grid gap-2">
              {loginErr && <ErrorBox message={loginErr} />}
              <Label>E-mail</Label>
              <Input value={d.profile.email} readOnly disabled />
              <Label htmlFor="reauth-pw">Mot de passe</Label>
              <Input
                id="reauth-pw"
                type="password"
                autoComplete="current-password"
                data-testid="input-reauth-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setTarget(null)}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                data-testid="button-reauth-confirm"
                disabled={signing || !password || !d.profile.email}
              >
                {signing && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Se reconnecter
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
